import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  ModalRoot,
  ModalPortal,
  ModalOverlay,
  ModalContent,
  ModalContentHeader,
  ModalContentBody,
  ModalContentFooter,
  ModalContentActions,
  ModalCancelButton,
  ModalActionButton,
} from '@socialchorus/shared-ui-components';
import { usePushToastOrFlashMessage } from '../toaster/deprecation-helper';
import UserGeneratedContentService from '../../../services/user-generated-content';

interface ArchiveConfirmDialogProps {
  close: () => void;
  contentId: number;
}

export function ArchiveConfirmDialog({
  close,
  contentId,
}: ArchiveConfirmDialogProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const { pushMessage } = usePushToastOrFlashMessage();
  const [isArchiving, setIsArchiving] = useState(false);

  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      const service = new UserGeneratedContentService(contentId);
      await service.archive();
      pushMessage({
        text: t('user_generated_content.archived', 'Post archived'),
        type: 'success',
      });
      close();
      history.replace('/');
    } catch (err) {
      console.error('UGC archive error', err);
      pushMessage({
        text: t('errors.default'),
        type: 'error',
      });
      close();
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <ModalRoot defaultOpen={true} onOpenChange={close}>
      <ModalPortal>
        <ModalOverlay />
        <ModalContent size="small">
          <ModalContentHeader
            title={t('content_submission.archive_title', 'Archive this post?')}
          />
          <ModalContentBody>
            {t(
              'content_submission.archive_confirm',
              'Are you sure you want to archive this post? It will be removed from the feed.'
            )}
          </ModalContentBody>
          <ModalContentFooter>
            <ModalContentActions>
              <ModalCancelButton label={t('common.cancel', 'Cancel')} />
              <ModalActionButton
                label={t('content_submission.archive', 'Archive')}
                onClick={handleArchive}
                disabled={isArchiving}
              />
            </ModalContentActions>
          </ModalContentFooter>
        </ModalContent>
      </ModalPortal>
    </ModalRoot>
  );
}

export const ID = 'Overlays.UGC_ARCHIVE_CONFIRM_DIALOG';
